import React, { useState, useRef, useEffect } from 'react';
import { Download, QrCode, Copy, Check } from 'lucide-react';
import './ToolsCommon.css';

const QRGenerator = () => {
  const [text, setText] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [size, setSize] = useState(300);
  const [bgColor, setBgColor] = useState('#ffffff');
  const [fgColor, setFgColor] = useState('#000000');
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef(null);

  const generateQR = () => {
    if (!text) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = size;
    canvas.height = size;

    // Simple QR code generation using canvas
    // For production, you'd want to use a library like qrcode
    const modules = createQRMatrix(text);
    const moduleSize = size / modules.length;

    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, size, size);

    ctx.fillStyle = fgColor;
    for (let row = 0; row < modules.length; row++) {
      for (let col = 0; col < modules[row].length; col++) {
        if (modules[row][col]) {
          ctx.fillRect(col * moduleSize, row * moduleSize, moduleSize, moduleSize);
        }
      }
    }

    setQrCode(canvas.toDataURL());
  };

  const createQRMatrix = (data) => {
    // Simple 21x21 QR code pattern (Version 1)
    const size = 21;
    const matrix = Array(size).fill(null).map(() => Array(size).fill(false));

    // Add finder patterns (corners)
    addFinderPattern(matrix, 0, 0);
    addFinderPattern(matrix, size - 7, 0);
    addFinderPattern(matrix, 0, size - 7);

    // Add timing patterns
    for (let i = 8; i < size - 8; i++) {
      matrix[6][i] = i % 2 === 0;
      matrix[i][6] = i % 2 === 0;
    }

    // Fill data (simplified - actual QR encoding is complex)
    let dataIndex = 0;
    for (let row = size - 1; row >= 0; row -= 2) {
      for (let col = size - 1; col >= 0; col--) {
        if (!isReserved(row, col, size)) {
          matrix[row][col] = dataIndex % 3 === 0; // Pseudo-random pattern
          dataIndex++;
        }
      }
    }

    return matrix;
  };

  const addFinderPattern = (matrix, row, col) => {
    for (let r = 0; r < 7; r++) {
      for (let c = 0; c < 7; c++) {
        if (
          r === 0 || r === 6 || c === 0 || c === 6 ||
          (r >= 2 && r <= 4 && c >= 2 && c <= 4)
        ) {
          matrix[row + r][col + c] = true;
        }
      }
    }
  };

  const isReserved = (row, col, size) => {
    // Check if position is in finder pattern or timing pattern
    if (row < 9 && col < 9) return true;
    if (row < 9 && col >= size - 8) return true;
    if (row >= size - 8 && col < 9) return true;
    if (row === 6 || col === 6) return true;
    return false;
  };

  const downloadQR = () => {
    const link = document.createElement('a');
    link.download = 'qrcode.png';
    link.href = qrCode;
    link.click();
  };

  const copyToClipboard = async () => {
    try {
      const blob = await (await fetch(qrCode)).blob();
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  useEffect(() => {
    if (text) {
      generateQR();
    }
  }, [text, size, bgColor, fgColor]);

  return (
    <div className="tool-container">
      <div className="tool-content-wrapper">
        <div className="tool-header">
          <QrCode size={48} className="tool-icon" />
          <h1 className="tool-title">QR Code Generator</h1>
          <p className="tool-subtitle">Create custom QR codes instantly</p>
        </div>

        <div className="tool-panel">
          <div className="tool-section">
            <label className="tool-label">Enter Text or URL</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Enter text, URL, or any data you want to encode..."
              className="tool-textarea"
              rows="4"
            />
          </div>

          <div className="tool-grid">
            <div className="tool-section">
              <label className="tool-label">Size: {size}px</label>
              <input
                type="range"
                min="200"
                max="600"
                value={size}
                onChange={(e) => setSize(Number(e.target.value))}
                className="tool-range"
              />
            </div>

            <div className="tool-section">
              <label className="tool-label">Background Color</label>
              <div className="color-input-group">
                <input
                  type="color"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                  className="tool-color-input"
                />
                <input
                  type="text"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                  className="tool-text-input"
                />
              </div>
            </div>

            <div className="tool-section">
              <label className="tool-label">Foreground Color</label>
              <div className="color-input-group">
                <input
                  type="color"
                  value={fgColor}
                  onChange={(e) => setFgColor(e.target.value)}
                  className="tool-color-input"
                />
                <input
                  type="text"
                  value={fgColor}
                  onChange={(e) => setFgColor(e.target.value)}
                  className="tool-text-input"
                />
              </div>
            </div>
          </div>

          {qrCode && (
            <div className="qr-preview">
              <img src={qrCode} alt="QR Code" className="qr-image" />
              <div className="tool-actions">
                <button onClick={downloadQR} className="tool-button primary">
                  <Download size={20} />
                  Download QR Code
                </button>
                <button onClick={copyToClipboard} className="tool-button secondary">
                  {copied ? <Check size={20} /> : <Copy size={20} />}
                  {copied ? 'Copied!' : 'Copy to Clipboard'}
                </button>
              </div>
            </div>
          )}

          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>
      </div>
    </div>
  );
};

export default QRGenerator;
